import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetActionByIdParams,
  GetActionByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetActionByIdState
  extends MergeRequestState<GetActionByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getActionById,
  isFetching: (state: ReduxState) => state.server.getActionById.isFetching,
  data: (state: ReduxState) => state.server.getActionById.data,
  error: (state: ReduxState) => state.server.getActionById.error,
};

const initialState: GetActionByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getActionById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetActionByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getActionById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetActionById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getActionById: ServerGetActionById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
